import { StatementPrs, StatementUt } from '@pimy-b2cweb/common'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { AxiosError } from 'axios'

export interface ErrorResponseData {
  code?: string
}

const encrypt = (text: string): string => {
  try {
    const encoder = new TextEncoder()
    const encodedBytes = encoder.encode(text)
    const bynaryString = Array.from(encodedBytes)
      .map((byte) => String.fromCharCode(byte))
      .join('')
    return btoa(bynaryString)
  } catch (e) {
    console.error('Encryption failed', e)
    return ''
  }
}

const decrypt = (text: string): string => {
  try {
    const bynaryString = atob(text)
    const byteArray = Uint8Array.from(bynaryString, (char) =>
      char.charCodeAt(0)
    )
    const decoder = new TextDecoder()
    return decoder.decode(byteArray)
  } catch (e) {
    console.error('Encryption failed', e)
    return ''
  }
}

export const isNumber = (val: string) => {
  return /^\d+$/.test(val)
}

export const isNumberOrLetter = (val: string) => {
  return /^[a-zA-Z0-9]+$/.test(val)
}

export const delay = (sec = 3000) => {
  return new Promise((resolve) => {
    setTimeout(resolve, sec)
  })
}

// export const convertMask = (str: string) => str.replace(/\*/g, '•')

export enum ErrorResponseCodeEnum {
  CanNotResendOtp = 'can-not-resend-otp',
  InternalServerError = 'internal_server_error',
  InvalidChallenge = 'invalid_challenge',
  InvalidMobileNo = 'invalid_mobile_no',
  InvalidOtpCode = 'invalid_otp_code',
  InvalidRequest = 'invalid_request',
  InvalidSession = 'invalid_session',
  TooManyAttempts = 'too_many_attempts',
  TooManyOtpRequests = 'too_many_otp_requests',
  UnauthorizedIdNo = 'unauthorized_id_no',
  UnknownError = 'unknown_error',
}

export enum StatementTypeCategory {
  // All = "all",
  UT = 'ut',
  PRS = 'prs',
  RETIREMENT_CALCULATOR = 'Retirement Calculator',
}

export enum StatementAccountTypeCategory {
  STATEMENT = 'Statement',
  ACCOUNT = 'Account',
  RETIREMENT_CALCULATOR = 'Retirement Calculator',
  PASSWORD_GUIDELINE = 'Password guideline',
}

export enum AccountTrxTypeCategory {
  ACCOUNT_DETAILS = 'Account Details',
  TRANSACTION_HISTORY = 'Transaction History',
  RSP = 'RSP',
  RWP = 'RWP',
  DISTRIBUTOR_DETAILS = 'Distributor Details',
}

export const CATEGORIZED_STATEMENT_TYPES = {
  [StatementTypeCategory.UT]: Object.values(StatementUt),
  [StatementTypeCategory.PRS]: Object.values(StatementPrs),
  [StatementTypeCategory.RETIREMENT_CALCULATOR]: [],
}

export const getErrorResponseCode = (mutatedError: unknown) => {
  const error = mutatedError as Partial<AxiosError<ErrorResponseData>>
  const _code = error?.response?.data?.code
  // if (!!_code && !/^HTTP-/.test(_code)) return _code
  if (
    _code &&
    Object.values(ErrorResponseCodeEnum).includes(
      _code as ErrorResponseCodeEnum
    )
  )
    return _code as ErrorResponseCodeEnum

  if (!!error?.response?.status) {
    switch (error.response.status) {
      case 400:
        return ErrorResponseCodeEnum.InvalidRequest
      case 403:
        return ErrorResponseCodeEnum.InvalidSession
      case 500:
      case 501:
        return ErrorResponseCodeEnum.InternalServerError
      default:
        return ErrorResponseCodeEnum.UnknownError
    }
  }
  if (error.code === 'ERR_NETWORK')
    return ErrorResponseCodeEnum.InternalServerError
  return ErrorResponseCodeEnum.UnknownError
}

export const getSessionStorage = (key: string) => {
  if (typeof window === 'undefined') return

  const encryptedData = sessionStorage.getItem(key)
  if (!encryptedData) return

  const decryptedData = decrypt(encryptedData)
  const jsonData = JSON.parse(decryptedData)

  return jsonData
}

export const setSessionStorage = (key: string, value: string) => {
  if (!key) return

  const jsonString = JSON.stringify(value)
  const encryptedData = encrypt(jsonString)

  sessionStorage.setItem(key, encryptedData)
}

export const removeSessionStorage = (key: string) => {
  sessionStorage.removeItem(key)
}

/* 
Function to format phone number
Input: Phone number with country code. e.g. 60123456789
Output: Phone number without cuntrycode. e.g. 123456789
 */
export const formatMobileNo = (mobileNo: string) => {
  let parsedNumber = mobileNo
  if (mobileNo.at(0) !== '+') parsedNumber = `+${mobileNo}`
  let parsed = parsePhoneNumber(parsedNumber)
  if (!parsed.valid) parsed = parsePhoneNumber(mobileNo, { regionCode: 'MY' })
  if (!parsed.valid) return mobileNo

  return (parsedNumber = parsed.number.significant)
}

export const contributionSummaryColumnMapping = {
  'PRS Plan Account No': 'accountNo',
  'Invest Approach': 'investmentApproach',
  'Sub Acc A (RM)': 'subAccA',
  'Sub Acc B (RM)': 'subAccB',
  'Total Contribution + Dividend (RM)': 'totalContribution',
  'Current Holding Value(RM)': 'totalContributionAmount',
}

export const accountDetailsColumnMapping = {
  'Fund Name': 'fundName',
  'Total Withdrawal (RM)': 'totalWithdrawal',
  'Total Switch In (RM)': 'totalSwitchIn',
  'Total Switch Out (RM)': 'totalSwitchOut',
  'Total Transfer In (RM)': 'totalTransferIn',
  'Total Transfer Out (RM)': 'totalTransferOut',
  'Current Holding Value (RM)': 'totalContributionAmount',
  'Current Holding Units': 'totalUnit',
  'NAV Per Unit (RM)': 'navPerUnit',
  'NAV Per Unit as at': 'navDate',
}

export const trxDetailsGhrPhrColumnMapping = {
  'Transaction Date': 'transactionDate',
  'Transaction Type': 'transactionType',
  'Gross Amount (RM)': 'grossAmount',
  'Sales Charges (RM)': 'salesCharge',
  'SST Amount (RM)': 'gstAmount',
  'PPA Opening Fee (RM)': 'ppaOpeningFee',
  'PPA Annual Fee (RM)': 'ppaAnnualFee',
  'Transfer Out (RM)': 'transferOutFee',
  'Withdrawal Penalty Tax (RM)': 'withdrawalPenaltyTax',
  'Net Amount (RM)': 'netAmount',
}

export const trxDetailsNonGhrPhrColumnMapping = {
  'Sub Account': 'subAccount',
  'Fund Name': 'fundName',
  'Transaction Date': 'transactionDate',
  'Transaction Type': 'transactionType',
  'Gross Amount (RM)': 'grossAmount',
  'PPA Fee (RM)': 'ppaFee',
  Charges: 'charges',
  'Fees (RM)': 'fees',
  'SST Amount (RM)': 'gstAmount',
  'Tax Penalty (RM)': 'taxPenalty',
  'Net Amount (RM)': 'netAmount',
  'NAV Per Unit (RM)': 'navPerUnit',
  Units: 'units',
}

export const rwpDetailsColumnMapping = {
  'Application Date': 'applicationDate',
  'Account No': 'accountNo',
  'Fund Name': 'fundName',
  'Withdrawal Preference': 'withDrawalPreference',
  Amount: 'amount',
  'No Of Payment': 'noOfPayment',
  'Frequency ': 'frequency',
  'RWP Start Date': 'rwpStartDate',
  'RWP End Date': 'rwpEndDate',
  'Bank Name': 'bankName',
  'Bank Account': 'bankAccount',
  'Branch ': 'branch',
  'Agent Code': 'agenetCode',
}

export const accountsType = {
  PRS_ACCOUNT: 'PRS Account',
  FIN_ACCOUNT: 'Fin Account',
}
